import React, { useState } from "react";
import { trodoAnalyticsTheme as theme } from "features/analytics/studio/theme";
import { formatNumber } from "shared/utils/numberFormatter";
import { makeFormatValue } from "features/analytics/studio/utils/axisFormat";
import {
  formatDurationSeconds,
} from "features/analytics/studio/utils/durationFormat";

/**
 * Funnel breakdown bar chart
 *
 * - One horizontal bar per breakdown segment
 * - Bars are sized by the *last step* value for that segment
 * - Supports multiple measuring types:
 *   - conversion_rate
 *   - unique_users
 *   - total_conversions
 *   - total_sessions
 *   - time_to_convert
 *
 * Props:
 * - steps: overall funnel steps array (used to know the last step index, first/last labels, etc.)
 * - breakdowns: array of breakdown objects from the API
 * - measuring: optional explicit measuring type; falls back to last step's `measuring` or "conversion_rate"
 *
 * Each breakdown is expected to look like:
 * {
 *   breakdownValue | breakdownKey,
 *   breakdownValues?, // array for hierarchical breakdowns (e.g. ["Android", "Abuja"])
 *   overallConversion?, // number (% from backend)
 *   steps: {
 *     step1: { count, value?, conversionRate?, measuring?, timeToConvert? },
 *     step2: { ... },
 *     ...
 *   }
 * }
 */
export default function FunnelBreakdownBarChart({
  steps = [],
  breakdowns = [],
  measuring: measuringProp,
  config,
}) {
  // Tooltip state: which segment is hovered (must be declared before any early returns)
  const [hoveredId, setHoveredId] = useState(null);

  const hasValidData =
    Array.isArray(steps) &&
    steps.length > 0 &&
    Array.isArray(breakdowns) &&
    breakdowns.length > 0;

  if (!hasValidData) {
    return (
      <div className="flex items-center justify-center h-[200px]">
        <div className="text-sm" style={{ color: theme.textMuted }}>
          No breakdown data available
        </div>
      </div>
    );
  }

  const firstStep = steps[0];
  const lastStep = steps[steps.length - 1];
  const lastStepKey = `step${steps.length}`;

  // Axis formatting: respect funnelAxis + left/right Y config
  const leftY = config?.axis?.leftY ?? {};
  const rightY = config?.axis?.rightY ?? {};
  const funnelAxis = config?.funnelAxis ?? "left";
  const yAxisConfig = funnelAxis === "right" ? rightY : leftY;
  const formatYValue = makeFormatValue(yAxisConfig);

  // Final measuring type
  const measuring =
    measuringProp ||
    lastStep?.measuring ||
    firstStep?.measuring ||
    "conversion_rate";

  // Helper: format time interval to compact string (used for time_to_convert)
  const formatTimeInterval = (interval) => {
    if (!interval && interval !== 0) return "—";
    if (typeof interval === "number" && !Number.isNaN(interval)) {
      return formatDurationSeconds(interval);
    }
    if (typeof interval === "string") return interval;
    if (typeof interval === "object" && interval !== null) {
      const { days = 0, hours = 0, minutes = 0, seconds = 0 } = interval;
      if (days > 0) {
        const dayFraction = days + hours / 24;
        return `${dayFraction.toFixed(1)}D`;
      }
      if (hours > 0) return `${hours}h`;
      if (minutes > 0) return `${minutes}m`;
      return `${seconds}s`;
    }
    return String(interval);
  };

  // Build funnel title used in tooltip header
  const firstLabel =
    firstStep?.label || firstStep?.eventName || "step 1";
  const lastLabel =
    lastStep?.label || lastStep?.eventName || `step ${steps.length}`;

  const measuringTitlePrefix = (() => {
    switch (measuring) {
      case "unique_users":
        return "Uniques of";
      case "total_conversions":
        return "Total Conversions of";
      case "total_sessions":
        return "Total Sessions of";
      case "property_sum":
        return "Value of";
      case "time_to_convert":
        return "Time to Convert for";
      case "conversion_rate":
      default:
        return "Conversion Rate of";
    }
  })();

  const tooltipTitle = `${measuringTitlePrefix} ${firstLabel} through ${lastLabel} – All Steps`;

  // Total users in last step (overall) – used for "% of overall" in tooltip
  const overallLastStepCount = Number(lastStep?.count || 0) || 0;

  // Flatten breakdowns into simple segments we can render as bars.
  const segments = breakdowns.map((bd, index) => {
    const baseLabel =
      (Array.isArray(bd.breakdownValues) && bd.breakdownValues.length > 0
        ? bd.breakdownValues.join(" / ")
        : null) ||
      bd.breakdownValue ||
      bd.breakdownKey ||
      "Unknown";

    const lastStepData = bd.steps?.[lastStepKey] || {};
    const count = Number(lastStepData.count || 0) || 0;

    const rawValue =
      typeof lastStepData.value === "number"
        ? lastStepData.value
        : undefined;

    const firstForBd = bd.steps?.step1 || {};
    const firstCount = Number(firstForBd.count || 0) || 0;

    const segmentOverallConv =
      typeof bd.overallConversion === "number"
        ? bd.overallConversion
        : firstCount > 0
          ? (count / firstCount) * 100
          : 0;

    const shareOfOverall =
      overallLastStepCount > 0
        ? (count / overallLastStepCount) * 100
        : 0;

    let numericForBar = 0;
    let primaryLabel = "";

    switch (measuring) {
      case "conversion_rate":
        numericForBar = segmentOverallConv;
        primaryLabel = `${segmentOverallConv.toFixed(1)}% • ${formatNumber(count)} users`;
        break;
      case "unique_users": {
        const v = rawValue != null ? rawValue : count;
        numericForBar = v;
        primaryLabel = `${formatNumber(v)} unique users`;
        break;
      }
      case "total_conversions": {
        const v = rawValue != null ? rawValue : 0;
        numericForBar = v;
        primaryLabel = `${formatNumber(v)} conversions`;
        break;
      }
      case "total_sessions": {
        const v = rawValue != null ? rawValue : 0;
        numericForBar = v;
        primaryLabel = `${formatNumber(v)} sessions`;
        break;
      }
      case "property_sum": {
        const v = rawValue != null ? rawValue : 0;
        numericForBar = v;
        primaryLabel = formatYValue(v);
        break;
      }
      case "time_to_convert": {
        const sec = Number(rawValue != null ? rawValue : 0);
        numericForBar = sec;
        primaryLabel = formatTimeInterval(sec);
        break;
      }
      default:
        numericForBar = segmentOverallConv;
        primaryLabel = `${segmentOverallConv.toFixed(1)}% • ${formatNumber(count)} users`;
    }

    return {
      id: index,
      label: baseLabel,
      users: count,
      primaryLabel,
      conversion: segmentOverallConv,
      shareOfOverall,
      numericForBar,
    };
  });

  const maxValue =
    segments.reduce(
      (max, s) => (s.numericForBar > max ? s.numericForBar : max),
      0
    ) || 1;

  const getTooltipLines = (segment) => {
    const conv = Number(segment.conversion || 0);
    const share = Number(segment.shareOfOverall || 0);

    switch (measuring) {
      case "conversion_rate":
        return [
          `${conv.toFixed(1)}% converted`,
          `${formatNumber(segment.users)} users (last step)`,
          `${share.toFixed(1)}% of overall users`,
        ];
      case "unique_users":
        return [
          `${formatNumber(segment.numericForBar)} unique users`,
          `${share.toFixed(1)}% of overall`,
        ];
      case "total_conversions":
        return [
          `${formatNumber(segment.numericForBar)} total conversions`,
          `${share.toFixed(1)}% of overall`,
        ];
      case "total_sessions":
        return [
          `${formatNumber(segment.numericForBar)} sessions`,
          `${share.toFixed(1)}% of overall`,
        ];
      case "property_sum":
        return [
          formatYValue(segment.numericForBar),
          `${share.toFixed(1)}% of overall`,
        ];
      case "time_to_convert":
        return [
          formatDurationSeconds(segment.numericForBar),
          `${formatNumber(segment.users)} completers`,
        ];
      default:
        return [
          `${conv.toFixed(1)}% converted`,
          `${formatNumber(segment.users)} users`,
        ];
    }
  };

  return (
    <div
      className="w-full space-y-3"
      style={{
        fontFamily: "Inter, sans-serif",
        backgroundColor: "var(--surface-base)",
      }}
    >
      {segments.map((segment) => {
        const widthPercent = Math.max(
          2,
          Math.min(100, (segment.numericForBar / maxValue) * 100)
        );
        const barColor = theme.primaryLight;
        const isHovered = hoveredId === segment.id;
        const tooltipLines = getTooltipLines(segment);

        // Side label: for conversion_rate show \"% • users\", for other types show the primary label (e.g. \"655 total conversions\")
        const sideLabel =
          measuring === "conversion_rate"
            ? `${segment.conversion.toFixed(1)}% • ${formatNumber(segment.users)} users`
            : segment.primaryLabel;

        return (
          <div
            key={segment.id}
            className="flex items-center gap-3 relative group"
            onMouseEnter={() => setHoveredId(segment.id)}
            onMouseLeave={() => setHoveredId(null)}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <span
                  className="text-sm font-medium truncate"
                  style={{ color: theme.textPrimary }}
                  title={segment.label}
                >
                  {segment.label}
                </span>
                <span className="text-xs" style={{ color: theme.textMuted }}>
                  {sideLabel}
                </span>
              </div>
              <div
                className="h-3 rounded-full overflow-hidden"
                style={{
                  backgroundColor: theme.bgOverlay || "var(--surface-hover)",
                }}
              >
                <div
                  className="h-full rounded-full transition-all duration-200"
                  style={{
                    width: `${widthPercent}%`,
                    backgroundColor: barColor,
                    boxShadow: isHovered
                      ? "0 4px 8px rgba(0,0,0,0.25)"
                      : "0 1px 2px rgba(0,0,0,0.15)",
                  }}
                />
              </div>
            </div>

            {/* Tooltip – positioned to the right of the bar */}
            {isHovered && (
              <div
                className="absolute z-40 bg-surface border shadow-lg rounded-lg px-3 py-2 text-xs"
                style={{
                  right: 0,
                  top: "100%",
                  marginTop: 8,
                  minWidth: 240,
                  maxWidth: 320,
                  borderColor: theme.border,
                }}
              >
                <div
                  className="mb-1 font-semibold"
                  style={{ color: theme.textPrimary }}
                >
                  {tooltipTitle}
                </div>
                <div
                  className="mb-1"
                  style={{ color: theme.textSecondary }}
                >
                  {segment.label}
                </div>
                {tooltipLines.map((line, idx) => (
                  <div
                    key={idx}
                    className="leading-snug"
                    style={{
                      color:
                        idx === 0
                          ? theme.textPrimary
                          : idx === 2
                          ? theme.textMuted
                          : theme.textPrimary,
                      marginTop: idx === 0 ? 2 : 0,
                    }}
                  >
                    {line}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
